import type { Bounds, DisplayList } from "../vendor/geometry/index.ts";
import type { LayerRecord } from "../vendor/parse/index.ts";
import {
  createCamera,
  fitBounds,
  panByScreen,
  pixelsToWorld,
  resize,
  screenToWorld,
  viewportBounds,
  worldToScreen,
  zoomAt,
  type Camera,
  type Point,
} from "../vendor/render/camera.ts";
import { DARK_STYLE, renderScene, type RenderStyle } from "../vendor/render/draw.ts";
import { rgbToCss } from "../vendor/render/colors.ts";
import { DARK_TOOLTIP, describePrimitive, drawTooltip, type TooltipStyle } from "../vendor/render/overlay.ts";
import { Scene } from "../vendor/render/scene.ts";
import { type Hit, hitTest } from "../vendor/render/spatial.ts";
import type { PartSummary } from "../worker/splitWorker.ts";

/**
 * The canvas.
 *
 * The drawing itself is rendered by the viewer's renderer, vendored unchanged.
 * What this adds is the part overlay — outlines, numbers, selection — and the
 * hit testing that turns a click into a part.
 *
 * Deliberately not the viewer's own `viewport.ts`, which also carries
 * measurement tools, a ruler and a compare mode. Composing the four modules we
 * actually need is less code than carrying three features we do not.
 */

export interface Overlay {
  readonly parts: readonly PartSummary[];
  readonly selected: ReadonlySet<number>;
  readonly hovered: number | null;
  /** Unclosed cut outlines, drawn in the warning colour. */
  readonly openContours: readonly {
    readonly outline: readonly Point[];
    /** The two free ends that never met — circled so the break is easy to find on the part. */
    readonly gapStart: Point;
    readonly gapEnd: Point;
  }[];
}

const EMPTY_OVERLAY: Overlay = {
  parts: [],
  selected: new Set(),
  hovered: null,
  openContours: [],
};

export interface ViewportStyle extends RenderStyle, TooltipStyle {
  readonly partOutline: string;
  readonly partOutlineSelected: string;
  readonly partFillSelected: string;
  readonly partLabel: string;
  readonly partLabelBackground: string;
  readonly openContour: string;
  /** The ring drawn around a gap in an open contour. */
  readonly gapMarker: string;
}

export const DARK_VIEWPORT: ViewportStyle = {
  ...DARK_STYLE,
  ...DARK_TOOLTIP,
  partOutline: "rgba(120, 170, 255, 0.55)",
  partOutlineSelected: "rgba(120, 200, 255, 1)",
  partFillSelected: "rgba(90, 160, 255, 0.16)",
  partLabel: "#e8eef8",
  partLabelBackground: "rgba(16, 22, 34, 0.82)",
  openContour: "rgba(255, 96, 96, 0.95)",
  gapMarker: "#ffffff",
};

/** Smallest a gap marker ring is ever drawn, in screen pixels — a tiny gap still needs to be findable. */
const GAP_MARKER_MIN_RADIUS = 14;
/** Extra room left between the two free ends and the ring drawn around them. */
const GAP_MARKER_PADDING = 8;

/** Below this many pixels across, a part gets no number drawn on it. */
const LABEL_MIN_SIZE = 26;

/** How close the pointer must be to an entity, in screen pixels, for the hover tooltip to pick it. */
const ENTITY_HIT_TOLERANCE_PX = 3;

export class Viewport {
  private camera: Camera;
  private scene: Scene | null = null;
  private overlay: Overlay = EMPTY_OVERLAY;
  private raf = 0;
  private readonly context: CanvasRenderingContext2D;
  private hoveredEntity: Hit | null = null;
  private cursor: Point | null = null;
  /** Primitive ids to stroke in the highlight colour — the Contents tab's entity focus. */
  private entityHighlight: ReadonlySet<number> = new Set();

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly style: ViewportStyle = DARK_VIEWPORT,
  ) {
    const context = canvas.getContext("2d", { alpha: false });
    if (context === null) {
      throw new Error("This browser did not provide a 2D canvas context.");
    }
    this.context = context;
    this.camera = createCamera(canvas.clientWidth || 1, canvas.clientHeight || 1);
  }

  /** Load a drawing. Resets the view to fit it. */
  load(display: DisplayList, layers: readonly LayerRecord[]): void {
    // The background is passed in because the renderer resolves ACI colour 7
    // against it — white geometry on a light background would be invisible.
    this.scene = new Scene(
      display,
      new Map(layers.map((l) => [l.name, l])),
      this.style.background,
    );
    if (display.extents) this.fit(display.extents);
    this.invalidate();
  }

  clear(): void {
    this.scene = null;
    this.overlay = EMPTY_OVERLAY;
    this.invalidate();
  }

  setOverlay(overlay: Overlay): void {
    this.overlay = overlay;
    this.invalidate();
  }

  /** Hide layers by name so their geometry stops drawing. Pure display state — never affects detection. */
  setHiddenLayers(hidden: ReadonlySet<string>): void {
    // A copy, not the caller's own set: `Scene.setHiddenLayers` keeps whatever
    // it is given by reference and diffs against it on the next call. Handing
    // it the live `state.hiddenLayers` object would let a later in-place
    // mutation of that set retroactively change what Scene thinks its own
    // previous value was, collapsing every diff after the first call to "no
    // change" and leaving later toggles invisible.
    this.scene?.setHiddenLayers(new Set(hidden));
    this.invalidate();
  }

  fit(bounds: Bounds): void {
    this.camera = fitBounds(this.camera, bounds);
    this.invalidate();
  }

  fitAll(): void {
    if (this.scene?.display.extents) this.fit(this.scene.display.extents);
  }

  /** Frame one part, with room around it. */
  fitPart(part: PartSummary): void {
    const pad = Math.max(part.width, part.height) * 0.25 + 1;
    this.fit({
      min: { x: part.bounds.minX - pad, y: part.bounds.minY - pad, z: 0 },
      max: { x: part.bounds.maxX + pad, y: part.bounds.maxY + pad, z: 0 },
    });
  }

  resized(): void {
    const width = this.canvas.clientWidth || 1;
    const height = this.canvas.clientHeight || 1;
    this.camera = resize(this.camera, width, height);

    // Backing store in device pixels, so the drawing is not soft on a
    // high-DPI screen; the transform below works in CSS pixels throughout.
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.round(width * ratio);
    this.canvas.height = Math.round(height * ratio);
    this.invalidate();
  }

  zoomAt(screen: Point, factor: number): void {
    this.camera = zoomAt(this.camera, screen, factor);
    this.invalidate();
  }

  pan(dx: number, dy: number): void {
    this.camera = panByScreen(this.camera, dx, dy);
    this.invalidate();
  }

  toWorld(screen: Point): Point {
    return screenToWorld(this.camera, screen);
  }

  /**
   * Which part is under a screen point.
   *
   * Tested against the real outline, not the bounding box, for the same reason
   * detection is: on a nest of L-shaped parts, box hit-testing would select a
   * part the cursor is nowhere near. Innermost wins, so a part sitting inside
   * another part's hole is selectable.
   */
  partAt(screen: Point): number | null {
    const world = this.toWorld(screen);
    let best: PartSummary | null = null;

    for (const part of this.overlay.parts) {
      if (
        world.x < part.bounds.minX || world.x > part.bounds.maxX ||
        world.y < part.bounds.minY || world.y > part.bounds.maxY
      ) {
        continue;
      }
      if (!pointInPolygon(world.x, world.y, part.outline)) continue;
      if (best === null || part.netArea < best.netArea) best = part;
    }

    return best?.index ?? null;
  }

  /**
   * Which entity (primitive) is under a screen point, within a small pixel
   * tolerance — for the hover tooltip. Unlike `partAt`, this hits the actual
   * DXF geometry (a line, an arc, ...) rather than a nested part's outline.
   */
  pickEntity(screen: Point): Hit | null {
    if (this.scene === null) return null;
    const world = this.toWorld(screen);
    const tolerance = pixelsToWorld(this.camera, ENTITY_HIT_TOLERANCE_PX);
    const scene = this.scene;
    return hitTest(scene.index, world.x, world.y, tolerance, (entry) => scene.isPickable(entry.id));
  }

  /** Set (or clear) the entity the tooltip should describe, and where to draw it. */
  setHoveredEntity(hit: Hit | null, cursor: Point | null): void {
    this.hoveredEntity = hit;
    this.cursor = cursor;
    this.invalidate();
  }

  /**
   * Highlight a set of primitives on top of the normal render — the Contents
   * tab's single-entity focus, set from either a precise canvas click or a
   * click on a line of raw text. Empty clears it.
   */
  setHighlightedEntities(ids: readonly number[]): void {
    this.entityHighlight = new Set(ids);
    this.invalidate();
  }

  /** True if `bounds` is at least partially inside the currently visible world-space rect. */
  isVisible(bounds: Bounds): boolean {
    const view = viewportBounds(this.camera);
    return !(
      bounds.max.x < view.min.x ||
      bounds.min.x > view.max.x ||
      bounds.max.y < view.min.y ||
      bounds.min.y > view.max.y
    );
  }

  /** Frame an arbitrary world-space box, padded like `fitPart()`. */
  focus(bounds: Bounds): void {
    const w = bounds.max.x - bounds.min.x;
    const h = bounds.max.y - bounds.min.y;
    const pad = Math.max(w, h) * 0.25 + 1;
    this.fit({
      min: { x: bounds.min.x - pad, y: bounds.min.y - pad, z: 0 },
      max: { x: bounds.max.x + pad, y: bounds.max.y + pad, z: 0 },
    });
  }

  /** World-space bounding box of a set of primitive ids, or null if none exist / no scene loaded. */
  boundsOfEntities(ids: readonly number[]): Bounds | null {
    if (this.scene === null) return null;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const id of ids) {
      const e = this.scene.index.entries[id];
      if (!e) continue;
      minX = Math.min(minX, e.minX);
      minY = Math.min(minY, e.minY);
      maxX = Math.max(maxX, e.maxX);
      maxY = Math.max(maxY, e.maxY);
    }
    if (minX > maxX) return null;
    return { min: { x: minX, y: minY, z: 0 }, max: { x: maxX, y: maxY, z: 0 } };
  }

  private invalidate(): void {
    if (this.raf !== 0) return;
    this.raf = requestAnimationFrame(() => {
      this.raf = 0;
      this.draw();
    });
  }

  private draw(): void {
    const ctx = this.context;
    const ratio = Math.min(window.devicePixelRatio || 1, 2);

    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.fillStyle = rgbToCss(this.style.background);
    ctx.fillRect(0, 0, this.camera.width, this.camera.height);

    if (this.scene !== null) {
      renderScene({
        ctx,
        camera: this.camera,
        scene: this.scene,
        style: this.style,
        devicePixelRatio: ratio,
        // The overlay is drawn afterwards on the same canvas, so the renderer
        // must not clear what it finds there.
        clear: false,
        highlighted: this.entityHighlight,
      });
    }

    this.drawOverlay(ctx);

    if (this.scene !== null && this.hoveredEntity !== null && this.cursor !== null) {
      drawTooltip(
        ctx,
        this.camera,
        this.style,
        ratio,
        describePrimitive(this.hoveredEntity.entry.primitive, this.scene.layerTable),
        this.cursor,
      );
    }
  }

  private drawOverlay(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    ctx.setTransform(
      Math.min(window.devicePixelRatio || 1, 2),
      0,
      0,
      Math.min(window.devicePixelRatio || 1, 2),
      0,
      0,
    );

    // Unclosed outlines first, so a selected part drawn over one does not hide
    // the very thing the user most needs to see.
    ctx.lineWidth = 2;
    ctx.strokeStyle = this.style.openContour;
    ctx.setLineDash([6, 4]);
    for (const open of this.overlay.openContours) {
      this.strokePolygon(ctx, open.outline, false);
    }
    ctx.setLineDash([]);

    // Circle each gap itself — the outline alone shows something is wrong
    // with the part, not where on it the two ends actually stop short.
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = this.style.gapMarker;
    for (const open of this.overlay.openContours) {
      this.strokeGapMarker(ctx, open.gapStart, open.gapEnd);
    }

    for (const part of this.overlay.parts) {
      const selected = this.overlay.selected.has(part.index);
      const hovered = this.overlay.hovered === part.index;

      if (selected) {
        ctx.fillStyle = this.style.partFillSelected;
        this.fillPolygon(ctx, part.outline);
      }

      ctx.lineWidth = selected || hovered ? 2 : 1;
      ctx.strokeStyle = selected || hovered
        ? this.style.partOutlineSelected
        : this.style.partOutline;
      this.strokePolygon(ctx, part.outline, true);
    }

    this.drawLabels(ctx);
    ctx.restore();
  }

  /**
   * Part numbers.
   *
   * Drawn after every outline so that no outline crosses a number, and skipped
   * entirely for parts too small on screen to read — a 114-part nest zoomed to
   * fit would otherwise be a solid mat of overlapping text.
   */
  private drawLabels(ctx: CanvasRenderingContext2D): void {
    ctx.font = "600 11px ui-sans-serif, system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    for (const part of this.overlay.parts) {
      const min = worldToScreen(this.camera, { x: part.bounds.minX, y: part.bounds.minY });
      const max = worldToScreen(this.camera, { x: part.bounds.maxX, y: part.bounds.maxY });
      const width = Math.abs(max.x - min.x);
      const height = Math.abs(max.y - min.y);
      if (width < LABEL_MIN_SIZE || height < LABEL_MIN_SIZE) continue;

      const cx = (min.x + max.x) / 2;
      const cy = (min.y + max.y) / 2;
      if (cx < -40 || cy < -20 || cx > this.camera.width + 40 || cy > this.camera.height + 20) {
        continue;
      }

      const text = String(part.index + 1);
      const metrics = ctx.measureText(text);
      const padX = 5;
      const boxWidth = metrics.width + padX * 2;
      const boxHeight = 16;

      ctx.fillStyle = this.style.partLabelBackground;
      ctx.beginPath();
      ctx.roundRect(cx - boxWidth / 2, cy - boxHeight / 2, boxWidth, boxHeight, 4);
      ctx.fill();

      ctx.fillStyle = this.style.partLabel;
      ctx.fillText(text, cx, cy + 0.5);
    }
  }

  /**
   * A ring around a contour's two free ends, sized to actually enclose them
   * so it reads as circling the break rather than as an unrelated dot, with a
   * floor so a near-zero gap is still easy to spot on screen.
   */
  private strokeGapMarker(ctx: CanvasRenderingContext2D, start: Point, end: Point): void {
    const a = worldToScreen(this.camera, start);
    const b = worldToScreen(this.camera, end);
    const cx = (a.x + b.x) / 2;
    const cy = (a.y + b.y) / 2;
    const half = Math.hypot(b.x - a.x, b.y - a.y) / 2;
    const radius = Math.max(half + GAP_MARKER_PADDING, GAP_MARKER_MIN_RADIUS);

    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.stroke();
  }

  private strokePolygon(
    ctx: CanvasRenderingContext2D,
    points: readonly Point[],
    close: boolean,
  ): void {
    if (points.length < 2) return;
    ctx.beginPath();
    const first = worldToScreen(this.camera, points[0]!);
    ctx.moveTo(first.x, first.y);
    for (let i = 1; i < points.length; i += 1) {
      const p = worldToScreen(this.camera, points[i]!);
      ctx.lineTo(p.x, p.y);
    }
    if (close) ctx.closePath();
    ctx.stroke();
  }

  private fillPolygon(ctx: CanvasRenderingContext2D, points: readonly Point[]): void {
    if (points.length < 3) return;
    ctx.beginPath();
    const first = worldToScreen(this.camera, points[0]!);
    ctx.moveTo(first.x, first.y);
    for (let i = 1; i < points.length; i += 1) {
      const p = worldToScreen(this.camera, points[i]!);
      ctx.lineTo(p.x, p.y);
    }
    ctx.closePath();
    ctx.fill();
  }
}

/** Ray-cast point-in-polygon, in screen-independent world coordinates. */
function pointInPolygon(x: number, y: number, polygon: readonly Point[]): boolean {
  let inside = false;
  const n = polygon.length;
  for (let i = 0, j = n - 1; i < n; j = i, i += 1) {
    const a = polygon[i]!;
    const b = polygon[j]!;
    if (a.y > y !== b.y > y) {
      const t = (y - a.y) / (b.y - a.y);
      if (x < a.x + t * (b.x - a.x)) inside = !inside;
    }
  }
  return inside;
}
